import type {
  AnalysisMode,
  CreateProductAnalysisInput,
  Currency,
  ExtractSeoProductRequest,
  GenerateSeoPromptRequest,
  Platform,
  SeoPromptGenerationResult,
  SeoProductExtractionResult,
  UserDecision,
} from '@rfz/contracts';

export type {
  SeoProductExtractionResult,
  SeoPromptGenerationResult,
  SeoPromptKeywordContext,
} from '@rfz/contracts';

export interface CurrentUser {
  id: string;
  username: string;
  role: 'admin' | 'user';
  isActive: boolean;
  version: number;
}

export interface KeywordRow {
  id: string;
  originalKeyword: string;
  chineseTranslation: string | null;
  standardProductName: string | null;
  category: string | null;
  currentDemand: number | null;
  stableDemand: number | null;
  wordstatVolume: number | null;
  supplyDemandRatio: number | null;
  trendRate: number | null;
  compositeScore: number | null;
  scoreCompleteness: number | null;
  hasHighRisk: boolean | null;
  isRedOcean: boolean | null;
  dataStatus: string;
  userDecision: UserDecision;
  updatedAt: string;
  version: number;
}

export interface JobSummary {
  id: string;
  jobType: string;
  platform: Platform | null;
  status: 'queued' | 'running' | 'paused' | 'cancelling' | 'cancelled' | 'succeeded' | 'failed';
  progress: number;
  succeededCount: number;
  failedCount: number;
  remainingCount: number;
  updatedAt: string;
  scope?: Record<string, unknown>;
  resultSummary?: Record<string, unknown>;
  errorSummary?: Record<string, unknown> | null;
  cancelledAt?: string | null;
  cancellationReason?: string | null;
  autoResume?: boolean;
  attempt?: number;
  createdAt?: string;
}

export interface WbSelectionRow {
  id: string;
  importBatchId: string;
  platformKeywordId: string | null;
  clusterId: string | null;
  sourceRowNumber: number;
  originalKeywordRu: string;
  normalizedKeyword: string;
  canonicalKeywordRu: string | null;
  chineseTranslationZh: string | null;
  standardProductNameZh: string | null;
  entityRole: string;
  attributes: Record<string, string | null>;
  brand: string | null;
  model: string | null;
  intentTerms: string[];
  useCases: string[];
  material: string | null;
  entityConfidence: number;
  entityEvidence: string[];
  translationStatus: string;
  entityStatus: string;
  categoryId: string | null;
  categoryNameRu: string | null;
  categoryNameZh: string | null;
  categoryConfidence: number | null;
  categorySource: string | null;
  categoryNeedsReview: boolean;
  frequencyBroad: number | null;
  frequencyPhrase: number | null;
  frequencyFixedForm: number | null;
  frequencyFixedOrder: number | null;
  frequencyExact: number | null;
  frequencyStatus: string;
  searchVolumeCurrent: number | null;
  searchVolumePrevious: number | null;
  clicks: number | null;
  carts: number | null;
  cartConversionRate: number | null;
  orderedItems: number | null;
  orderConversionRate: number | null;
  orderedProducts: number | null;
  productsCount: number | null;
  demandTrendRate: number | null;
  demandSupplyRatio: number | null;
  orderSupplyRatio: number | null;
  searchToOrderRate: number | null;
  demandPercentile: number | null;
  conversionPercentile: number | null;
  supplyOpportunityPercentile: number | null;
  seasonalityType: string;
  estimatedPeakPeriod: string | null;
  seasonalityStrength: number | null;
  seasonalityConfidence: number | null;
  seasonalityEvidence: string[];
  riskAction: string;
  riskLevel: string;
  riskTags: string[];
  riskReason: string | null;
  riskRuleIds: string[];
  riskGroups: string[];
  riskSourceIds: string[];
  riskDictionaryVersion: string | null;
  riskMatchEvidence: Array<Record<string, unknown>>;
  riskVerifiedAt: string | null;
  hardReject: boolean;
  preliminaryScore: number | null;
  preliminaryRank: number | null;
  preliminaryDecision: string;
  preliminaryScoreBreakdown: Record<string, number | null>;
  finalScore: number | null;
  finalDecision: string | null;
  enrichmentStage: string;
  enrichmentCompleteness: number;
  scoreConfidence: number;
  clusterConfidence: number;
  clusterNeedsReview: boolean;
  opportunityScore: number | null;
  scoreCompleteness: number;
  scoreComponents: Record<string, number>;
  selectionDecision: string;
  decisionReason: string | null;
  positiveSignals: string[];
  negativeSignals: string[];
  missingData: string[];
  simpleDecision: 'selectable' | 'caution' | 'reject' | 'risk_excluded' | 'data_incomplete';
  dataConclusion:
    'data_priority' | 'data_watch' | 'data_reject' | 'data_incomplete' | 'risk_excluded';
  simpleDecisionReason: string | null;
  demandLevel: 'high' | 'medium' | 'low' | null;
  conversionLevel: 'strong' | 'medium' | 'weak' | null;
  competitionLevel: 'low_competition' | 'normal_competition' | 'crowded' | null;
  simplePositiveSignals: string[];
  simpleNegativeSignals: string[];
  simpleMissingData: string[];
  simpleDecisionUpdatedAt: string | null;
  dataStatus: string;
  processingStage: string;
  failureStage: string | null;
  failureCode: string | null;
  errorSummary: string | null;
  processingLog: Array<Record<string, unknown>>;
  sourceSnapshot: Record<string, unknown>;
  fieldUpdatedAt: Record<string, string>;
  userDecision: UserDecision;
  updatedAt: string;
  version: number;
}

export interface WbSelectionSummary {
  batchId: string | null;
  total: number;
  processing: number;
  dataComplete: number;
  priorityTest: number;
  smallTest: number;
  watch: number;
  reject: number;
  riskReject: number;
  dataIncomplete: number;
  processingFailed: number;
  preliminaryScored: number;
  preliminaryPriority: number;
  preliminaryEnrich: number;
  depthCompleted: number;
  awaitingDepth: number;
  preliminaryRisk: number;
  actionable: number;
  complianceReview: number;
  categoryReview: number;
  dataAnomaly: number;
  highDemandLowSupply: number;
  highConversion: number;
  selectable: number;
  caution: number;
  riskClear: number;
  simpleReject: number;
  simpleRiskExcluded: number;
  simpleDataIncomplete: number;
  highDemandLowCompetition: number;
  dataPriority: number;
  dataWatch: number;
  dataReject: number;
  riskExcluded: number;
  selectionEligible: number;
  selectionRiskFiltered: number;
}

export type WbSelectionView =
  | 'all'
  | 'data_priority'
  | 'selectable'
  | 'clear'
  | 'caution'
  | 'high_demand_low_competition'
  | 'high_conversion'
  | 'risk_excluded'
  | 'data_incomplete'
  | 'reject';

export interface WbSelectionEntityCluster {
  id: string;
  clusterKey: string;
  canonicalKeywordRu: string;
  chineseTranslationZh: string | null;
  standardProductNameZh: string | null;
  entityRole: string;
  attributes: Record<string, string | null>;
  brand: string | null;
  model: string | null;
  intentTerms: string[];
  useCases: string[];
  material: string | null;
  longTailQueries: string[];
  clusterConfidence: number;
  clusterNeedsReview: boolean;
  memberCount: number;
  aiStatus: string;
  topvisorStatus: string;
  frequencyBroad: number | null;
  frequencyPhrase: number | null;
  frequencyFixedForm: number | null;
  frequencyFixedOrder: number | null;
  frequencyExact: number | null;
  riskLevel: string;
  riskTags: string[];
  riskReason: string | null;
  riskRuleIds: string[];
  riskGroups: string[];
  riskSourceIds: string[];
  riskDictionaryVersion: string | null;
  riskMatchEvidence: Array<Record<string, unknown>>;
  riskVerifiedAt: string | null;
  hardReject: boolean;
}

export interface WbDepthSelection {
  mode: 'selected' | 'filtered' | 'top' | 'category' | 'threshold';
  candidateIds: string[];
  text: string;
  preliminaryDecisions: string[];
  riskLevels: string[];
  categoryName: string | null;
  minPreliminaryScore: number | null;
  limit: 100 | 500 | 1000 | 2000;
  concurrencyLimit: number;
  runAi: boolean;
  runTopvisor: boolean;
}

export interface WbDepthEstimate {
  batchId: string;
  batchCandidateCount: number;
  preliminaryScoredCount: number;
  clusteredCandidateCount: number;
  rawSearchCount: number;
  uniqueEntityClusters: number;
  estimatedAiCalls: number;
  estimatedTopvisorKeywords: number;
  cachedTopvisorKeywords: number;
  estimatedTopvisorCalls: number;
  alreadyDeepEnriched: number;
  lowConfidenceClusters: number;
  batchLimit: number;
  concurrencyLimit: number;
  currencyCost: number | null;
  costNote: string;
  blockedReason: string | null;
}

export type SpreadsheetField =
  | 'keyword'
  | 'chineseTranslation'
  | 'standardProductName'
  | 'category'
  | 'currentDemand'
  | 'searchVolumePrevious'
  | 'clicks'
  | 'carts'
  | 'cartConversionRate'
  | 'orderedItems'
  | 'orderConversionRate'
  | 'orderedProducts'
  | 'productsCount'
  | 'dataDate'
  | 'stableDemand'
  | 'wordstatVolume'
  | 'supplyCount'
  | 'demandCount'
  | 'supplyDemandRatio';

export interface ImportInspection {
  uploadId: string;
  file: {
    name: string;
    size: number;
    sha256: string;
    selectedSheetName: string | null;
    headerRowNumber: number;
    dataStartRow: number;
    usedRange: string | null;
    headers: string[];
  };
  mapping: Record<
    SpreadsheetField,
    {
      field: SpreadsheetField;
      index: number | null;
      header: string | null;
      confidence: number;
      source: 'exact' | 'contains' | 'unrecognized';
    }
  >;
  wbJamProfile: null | {
    profileId: string;
    selectedSheetName: string;
    headerRow: number;
    dataStartRow: number;
    usedRange: string;
    fieldMapping: Record<
      string,
      {
        field: string;
        index: number | null;
        header: string | null;
        column: string;
        source: 'exact' | 'alias' | 'unrecognized';
      }
    >;
    matchedHeaders: string[];
    unmatchedHeaders: string[];
    selectedPeriod: string | null;
    comparisonPeriod: string | null;
    categoryScope: string[];
  };
  counts: {
    originalRows: number;
    validRows: number;
    emptyRows: number;
    duplicateRows: number;
    existingRows: number;
    newRows: number;
    unrecognizedRows: number;
  };
  invalidReasonDistribution: Array<{
    code: 'MISSING_KEYWORD' | 'NON_RUSSIAN_KEYWORD' | 'MISSING_CURRENT_SEARCH_VOLUME';
    label: string;
    count: number;
  }>;
  invalidRows: Array<{
    rowNumber: number;
    reasonCode: 'MISSING_KEYWORD' | 'NON_RUSSIAN_KEYWORD' | 'MISSING_CURRENT_SEARCH_VOLUME';
    reason: string;
    originalKeyword: string | null;
    normalizedKeyword: string | null;
    currentSearchVolume: string | number | boolean | null;
    values: Array<string | number | boolean | null>;
  }>;
  preview: Array<{
    rowNumber: number;
    recognized: boolean;
    normalizedKeyword: string | null;
    values: Partial<Record<SpreadsheetField, string | number | boolean | null>>;
  }>;
}

export interface ImportBatchSummary {
  id: string;
  platform: Platform;
  originalFileName: string;
  fileSize: number;
  status: string;
  isActive: boolean;
  addedCount: number;
  updatedCount: number;
  duplicateCount: number;
  conflictCount: number;
  issueCount: number;
  failedCount: number;
  originalRows: number;
  validRows: number;
  emptyRows: number;
  existingRows: number;
  newRows: number;
  unrecognizedRows: number;
  blacklistSkippedCount: number;
  undoExpiresAt: string;
  undoneAt: string | null;
  createdAt: string;
  completedAt: string | null;
  cancelRequestedAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
  expectedRows: number | null;
  importedRows: number;
  version: number;
}

export interface BlacklistSummary {
  id: string;
  scope: string;
  platform: Platform | null;
  normalizedKeyword: string;
  reason: string;
  releasedAt: string | null;
  version: number;
}

export interface DictionarySummary {
  id: string;
  canonicalKeyword: string;
  normalizedKeyword: string;
  chineseTranslation: string | null;
  standardProductName: string | null;
  productType: string | null;
  mainKeyword: string | null;
  sourceKind: string;
  confidence: number | null;
  locked: boolean;
  version: number;
}

export interface PromptSummary {
  id: string;
  taskType: string;
  name: string;
  description: string | null;
  versions: Array<{
    id: string;
    versionNumber: number;
    isActive: boolean;
    createdAt: string;
  }>;
}

export interface AdminNotification {
  id: string;
  notificationType: string;
  severity: string;
  title: string;
  summary: string;
  resourceType: string | null;
  resourceId: string | null;
  acknowledgedAt: string | null;
  createdAt: string;
}

export interface ProductCardDraft {
  platform: Platform;
  title: string;
  description: string;
  attributes: Record<string, string>;
  keywords: string[];
  category: string | null;
  sourceStatus: 'live' | 'category_cache' | 'platform_default';
  missingAttributes: boolean;
}

export interface ProductAnalysisDetail {
  analysis: {
    id: string;
    productId: string;
    inputMode: 'simple' | 'full';
    analysisMode: AnalysisMode;
    platformScope: Platform[];
    sourcePlatform: Platform | null;
    sourceKeywordId: string | null;
    currentVersionId: string | null;
    status: string;
    version: number;
  };
  product: {
    id: string;
    canonicalName: string;
    sourceKeyword: string | null;
    productLink: string | null;
    sku: string | null;
    material: string | null;
    dimensions: string | null;
    structure: string | null;
  };
  currentVersion: {
    id: string;
    versionNumber: number;
    inputSnapshot: CreateProductAnalysisInput;
    missingFields: string[];
    restoredFromVersionId: string | null;
    createdAt: string;
  } | null;
  versions: Array<{
    id: string;
    versionNumber: number;
    restoredFromVersionId: string | null;
    createdAt: string;
  }>;
  results: Array<{
    id: string;
    platform: Platform;
    conclusion: 'worth_testing' | 'cautious_testing' | 'not_recommended' | 'insufficient_data';
    shortReason: string;
    triggeredRules: string[];
    marketSnapshot: Record<string, unknown>;
    riskSnapshot: Record<string, unknown>;
    dataSources: Array<Record<string, unknown>>;
    missingFields: string[];
    externalStatus: string;
    ruleVersion: string;
    logistics: {
      actualWeightKg: number | null;
      volumeWeightKg: number | null;
      chargeableWeightKg: number | null;
      automaticCost: number | null;
      manualOverrideCost: number | null;
      adoptedCost: number | null;
      currency: Currency;
      missingFields: string[];
    } | null;
    profit: {
      totalCost: number | null;
      receivedRevenue: number | null;
      profit: number | null;
      grossMarginRate: number | null;
      breakEvenPrice: number | null;
      targetMarginPrice: number | null;
      currency: Currency;
      completeness: number;
      missingFields: string[];
    } | null;
  }>;
  sourceObservations: Array<{
    id: string;
    sourceKind: string;
    sourceStatus: string;
    platform: Platform | null;
    errorSummary: Record<string, unknown> | null;
    createdAt: string;
  }>;
}

export interface ProductIdeaInterpretation {
  inputLanguage: 'zh' | 'ru';
  entity: string;
  productType: string | null;
  useCases: string[];
  confidence: number;
  ambiguities: string[];
  expressions: Array<{
    russian: string;
    chineseBackTranslation: string | null;
    confidence: number;
    source: 'dictionary' | 'ai' | 'machine_translation' | 'user_input';
  }>;
  marketAnalysis?: {
    eacAssessment: {
      judgment: 'required' | 'not_required' | 'uncertain';
      reasonZh: string;
    } | null;
    seasonalityAnalysis: {
      trend: 'all_year_stable' | 'quarter_peak' | 'month_peak' | 'holiday_peak' | 'unknown';
      descriptionZh: string;
      peakMonths: number[];
    } | null;
  } | null;
  decomposition?: {
    primaryProduct: string;
    secondaryProducts: string[];
    attributes: string[];
    useCases: string[];
    excludedTerms: string[];
    corePhrases: string[];
    method: 'rules' | 'dictionary' | 'deepseek';
  };
}

export type ResearchSuggestSourceType =
  'local_dictionary' | 'yandex_suggest' | 'google_suggest' | 'wb_suggest' | 'ai_supplement';

export interface ResearchSuggestion {
  id: string;
  keywordRu: string;
  translationZh: string | null;
  relevance: number;
  sources: Array<{
    type: ResearchSuggestSourceType;
    rank: number | null;
    collectedAt: string;
  }>;
  sourceCount: number;
  verifiedBySuggest: boolean;
  suggestStrength: 'strong' | 'medium' | 'weak' | 'ai_only';
  selected: boolean;
  platformKeywordIds: Partial<Record<Platform, string>>;
  wordstatStatus: 'not_checked' | 'opened' | 'manually_verified' | 'no_reliable_data';
  wordstatFrequency: number | null;
  wordstatPeriod: string | null;
  wordstatTrend: 'rising' | 'stable' | 'declining' | 'seasonal' | 'insufficient' | null;
  wordstatNotes: string | null;
  wordstatOpenedAt: string | null;
  wordstatVerifiedAt: string | null;
  wordstatUrl: string;
  updatedAt: string;
  version: number;
}

export interface ResearchProviderStatus {
  provider: 'yandex_suggest' | 'google_suggest' | 'wb_suggest';
  status: 'ready' | 'stale' | 'blocked' | 'unavailable';
  itemCount: number;
  cached: boolean;
  collectedAt: string;
  endpoint: string;
  failureCode: string | null;
  failureReason: string | null;
}

export interface ResearchRun {
  analysis: {
    id: string;
    status: string;
    analysisMode: AnalysisMode;
    platforms: Platform[];
    version: number;
    createdAt: string;
    updatedAt: string;
  };
  product: { id: string; canonicalName: string };
  state: {
    idea: string;
    entityZh: string;
    productTypeZh: string | null;
    useCases: string[];
    coreQueries: Array<{
      keywordRu: string;
      translationZh: string | null;
      confidence: number;
    }>;
    providerStatuses: ResearchProviderStatus[];
    decision:
      | 'worth_verifying'
      | 'cautious'
      | 'competition_high'
      | 'weak_signal'
      | 'insufficient_data'
      | null;
    decisionReasons: Array<{ category: string; summary: string }>;
  };
  suggestions: ResearchSuggestion[];
  config: {
    wordstatRegion: string;
    wordstatBaseUrl: string;
    suggestCacheHours: number;
  };
  stages?: string[];
}

export type WordstatVolumeStatus =
  'pending' | 'queued' | 'running' | 'completed' | 'cache_hit' | 'failed' | 'insufficient_balance';

export interface WordstatVolumeSnapshot {
  configured: boolean;
  region: { regionKey: string; name: string } | null;
  source: 'Topvisor / Yandex Wordstat';
  completeness: number;
  job: JobSummary | null;
  items: Array<{
    keywordRu: string;
    normalizedKeyword: string;
    status: WordstatVolumeStatus;
    cached: boolean;
    queriedAt: string | null;
    expiresAt: string | null;
    errorCode: string | null;
    volumes: Array<{
      type: 1 | 2 | 3 | 5 | 6;
      volume: number | null;
      hasValue: boolean;
      status: WordstatVolumeStatus;
      cached: boolean;
      queriedAt: string | null;
    }>;
  }>;
}

export interface SeasonalityEstimate {
  keywordRu: string;
  region: string;
  templateId:
    | 'summer_strong'
    | 'winter_strong'
    | 'spring_garden'
    | 'new_year'
    | 'school_season'
    | 'gift_season'
    | 'all_year_stable'
    | 'weak_seasonal'
    | 'unknown';
  templateLabel: string;
  templateVersion: string;
  lexiconVersion: string;
  confidence: 'high' | 'medium' | 'low';
  confidenceScore: number;
  seasonType: string;
  expectedSeason: string;
  expectedPeak: string;
  peakMonths: number[];
  currentHeat: number;
  currentPhraseVolume: number | null;
  volumeAvailable: boolean;
  annualVariationLabel: string;
  supportingSignals: string[];
  conflictingSignals: string[];
  suggestAvailable: boolean;
  aiHypothesis: {
    templateId: SeasonalityEstimate['templateId'];
    peakMonths: number[];
    confidence: number;
    reasonZh: string;
    assumptionsZh: string[];
    model: string;
  } | null;
  fallbackMessage: string | null;
  explanation: string;
  decisionBasis: {
    productEntity: string;
    standardProductNameZh: string | null;
    entityRole: string | null;
    categoryLabel: string;
    templateId: SeasonalityEstimate['templateId'];
    validSuggestCount: number;
    matchedSeasonSignals: string[];
    conflictingSeasonSignals: string[];
    currentPhraseVolume: number | null;
    aiTemplateId: SeasonalityEstimate['templateId'] | null;
    aiPeakMonths: number[];
    aiConfidence: number | null;
    aiReasonZh: string | null;
    confidenceReason: string;
    conclusion: string;
  };
  calculatedAt: string;
  months: Array<{
    month: number;
    monthKey: string;
    label: string;
    index: number;
    estimatedVolume: number | null;
    currentAnchor: boolean;
    dataType: 'model_estimate';
  }>;
  cache: { hit: boolean; expiresAt: string };
  suggest: {
    available: boolean;
    queryCount: number;
    requestCount: number;
    cachedQueryCount: number;
    evidenceCount: number;
    evidenceHash: string;
  };
  source: {
    currentFrequency: string;
    seasonSignals: string;
    curve: string;
  };
}

export interface CostProfileSummary {
  id: string;
  platform: Platform;
  fulfillmentMethod: string;
  name: string;
  defaultCurrency: Currency;
  config: Record<string, unknown>;
  isActive: boolean;
}

export interface ExchangeRateSummary {
  id: string;
  baseCurrency: Currency;
  quoteCurrency: Currency;
  rate: number;
  source: string;
  rateDate: string;
  status: string;
}

export interface KeywordDetail {
  keyword: KeywordRow;
  dictionary: DictionarySummary | null;
  risk: { tags?: Array<{ code: string; severity: string; label: string }> } | null;
  score: { components?: Array<{ key: string; score: number | null; explanation: string }> } | null;
  competitor: {
    latestAttempt: { status?: string; sourceStatus?: string; errorSummary?: unknown } | null;
    latestSuccess: { fetchedAt?: string; expiresAt?: string; sourceStatus?: string } | null;
    stale: boolean;
    items: Array<Record<string, unknown>>;
    sampleSet: { summary?: Record<string, unknown>; missingRealSales?: boolean } | null;
    sampleItems: Array<{
      sample: {
        id: string;
        selected: boolean;
        locked: boolean;
        exclusionReason: string | null;
        version: number;
      };
      item: {
        id: string;
        title: string | null;
        price: number | null;
        reviewCount: number | null;
        link: string;
      } | null;
    }>;
  };
  material: { id: string; rawText: string } | null;
  attributes: Array<{
    id: string;
    name: string;
    value: string;
    sourceExcerpt: string;
    version: number;
  }>;
  relatedKeywords: Array<{
    id: string;
    keywordText: string;
    chineseTranslation: string | null;
    usageKind: 'title' | 'description' | 'attribute' | 'unrelated' | 'risk' | 'duplicate';
    selected: boolean;
    relevance: number | null;
  }>;
  productCards: Array<Record<string, unknown>>;
  automaticRefresh?: { jobId: string; fetchId: string } | null;
}

let csrfToken = '';

export const AUTH_REQUIRED_EVENT = 'rfz:auth-required';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Empty POST streams can acquire chunked framing in the Pages/Tunnel proxy,
  // which Fastify cannot parse without a media type. Send explicit JSON instead.
  if (init.method?.toUpperCase() === 'POST' && init.body == null) {
    init = { ...init, body: '{}' };
  }
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof Blob) && !(init.body instanceof ArrayBuffer)) {
    headers.set('Content-Type', 'application/json');
  }
  if (csrfToken && init.method && init.method !== 'GET') headers.set('X-CSRF-Token', csrfToken);
  const target = path.startsWith('/api/') ? path : `/api/v1${path}`;
  const response = await fetch(target, { ...init, headers, credentials: 'include' });
  if (response.status === 401) window.dispatchEvent(new Event(AUTH_REQUIRED_EVENT));

  if (response.status === 204) return undefined as T;

  const rawBody = await response.text();
  if (!rawBody.trim()) {
    throw new Error(`服务器返回空响应（HTTP ${response.status}），请稍后重试`);
  }

  let body: unknown;
  try {
    body = JSON.parse(rawBody);
  } catch {
    throw new Error(`服务器返回了无法识别的响应（HTTP ${response.status}），请稍后重试`);
  }

  if (!body || typeof body !== 'object') {
    throw new Error(`请求失败（HTTP ${response.status}）`);
  }

  const result = body as {
    data?: T;
    message?: string;
    error?: { message?: string };
  };
  if (!response.ok || result.error) {
    throw new Error(
      result.error?.message || result.message || `请求失败（HTTP ${response.status}）`,
    );
  }
  if (!('data' in result)) throw new Error('服务器响应缺少数据，请稍后重试');
  return result.data;
}

export async function login(input: { username: string; password: string; rememberMe: boolean }) {
  const result = await request<{ user: CurrentUser; csrfToken: string }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  csrfToken = result.csrfToken;
  return result.user;
}

export async function register(input: { username: string; password: string }) {
  return request<CurrentUser>('/auth/register', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function logout() {
  await request('/auth/logout', { method: 'POST' });
  csrfToken = '';
}

export async function getCurrentUser() {
  const result = await request<{ user: CurrentUser; csrfToken: string }>('/auth/me');
  csrfToken = result.csrfToken;
  return result.user;
}

export async function searchKeywords(platform: Platform, text: string) {
  return request<{ items: KeywordRow[]; nextCursor: string | null }>(
    `/platforms/${platform}/keywords/search`,
    {
      method: 'POST',
      body: JSON.stringify({
        query: { text, mode: 'all_words', exclude: [], scope: 'current_platform' },
        filters: {
          decisions: ['unmarked', 'keep'],
          includeHighRisk: false,
          includeDiscarded: false,
        },
        sort: [{ field: 'updatedAt', direction: 'desc' }],
        cursor: null,
        limit: 50,
      }),
    },
  );
}

export async function updateDecision(platform: Platform, row: KeywordRow, decision: UserDecision) {
  return request<KeywordRow>(`/platforms/${platform}/keywords/${row.id}/decision`, {
    method: 'PATCH',
    body: JSON.stringify({ decision, expectedVersion: row.version }),
  });
}

export async function updateDecisions(
  platform: Platform,
  rows: KeywordRow[],
  decision: UserDecision,
) {
  return request<{ updated: KeywordRow[]; conflicts: string[] }>(
    `/platforms/${platform}/keywords/decisions`,
    {
      method: 'PATCH',
      body: JSON.stringify({
        decision,
        records: rows.map((row) => ({ id: row.id, expectedVersion: row.version })),
      }),
    },
  );
}

export async function getPreference(scope: string) {
  return request<Record<string, unknown>>(`/preferences/${encodeURIComponent(scope)}`);
}

export async function savePreference(scope: string, value: Record<string, unknown>) {
  return request<Record<string, unknown>>(`/preferences/${encodeURIComponent(scope)}`, {
    method: 'PUT',
    body: JSON.stringify(value),
  });
}

export async function createExport(platform: Platform, selectedIds: string[], columns: string[]) {
  return request<{ jobId: string; downloadToken: string; expiresInMinutes: number }>(
    `/platforms/${platform}/exports`,
    {
      method: 'POST',
      body: JSON.stringify({
        range: selectedIds.length ? 'selected' : 'all_non_discarded',
        selectedIds,
        columns,
        filters: {},
      }),
    },
  );
}

async function sha256(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, '0')).join('');
}

export async function uploadSpreadsheet(
  platform: Platform,
  file: File,
  onProgress: (progress: number) => void,
) {
  const chunkSize = 8 * 1024 * 1024;
  const checksum = await sha256(file);
  const upload = await request<{ uploadId: string; receivedChunks: number[] }>('/imports/uploads', {
    method: 'POST',
    body: JSON.stringify({
      platform,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type || 'application/octet-stream',
      checksum,
      chunkSize,
    }),
  });
  const chunks = Math.ceil(file.size / chunkSize);
  for (let index = 0; index < chunks; index += 1) {
    if (upload.receivedChunks.includes(index)) continue;
    const start = index * chunkSize;
    const end = Math.min(file.size, start + chunkSize);
    await request(`/imports/uploads/${upload.uploadId}/chunks/${index}`, {
      method: 'PUT',
      headers: {
        'Content-Range': `bytes ${start}-${end - 1}/${file.size}`,
        'Content-Type': 'application/octet-stream',
      },
      body: file.slice(start, end),
    });
    onProgress((index + 1) / chunks);
  }
  return request<ImportInspection>(`/imports/uploads/${upload.uploadId}/inspect`, {
    method: 'POST',
  });
}

export function exportInvalidImportRows(inspection: ImportInspection) {
  const quote = (value: unknown) => {
    const text =
      value == null
        ? ''
        : typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
          ? String(value)
          : (JSON.stringify(value) ?? '');
    return `"${text.replaceAll('"', '""')}"`;
  };
  const headers = ['Excel行号', '无效代码', '无效原因', ...inspection.file.headers];
  const lines = inspection.invalidRows.map((row) =>
    [row.rowNumber, row.reasonCode, row.reason, ...row.values].map(quote).join(','),
  );
  const blob = new Blob([`\uFEFF${headers.map(quote).join(',')}\n${lines.join('\n')}`], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const stem = inspection.file.name.replace(/\.[^.]+$/u, '').replace(/[^\p{L}\p{N}._-]+/gu, '-');
  link.download = `${stem || 'wb-jam'}-invalid-rows.csv`;
  link.click();
  URL.revokeObjectURL(url);
  return inspection.invalidRows.length;
}

export async function confirmSpreadsheetImport(
  uploadId: string,
  headerRowNumber: number,
  fieldMapping: Partial<Record<SpreadsheetField, number | null>>,
) {
  return request<{ batchId: string; jobId: string }>(`/imports/uploads/${uploadId}/finalize`, {
    method: 'POST',
    body: JSON.stringify({ headerRowNumber, fieldMapping }),
  });
}

export async function searchWbSelection(input: {
  batchId?: string | null;
  text?: string;
  decisions?: string[];
  dataStatuses?: string[];
  riskLevels?: string[];
  preliminaryDecisions?: string[];
  minSearchVolume?: number | null;
  minOrderedItems?: number | null;
  minOrderConversionRate?: number | null;
  maxProductsCount?: number | null;
  minDemandSupplyRatio?: number | null;
  view?: WbSelectionView;
  limit?: number;
  offset?: number;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
}) {
  const query = new URLSearchParams();
  if (input.batchId) query.set('batchId', input.batchId);
  if (input.text) query.set('text', input.text);
  if (input.decisions?.length) query.set('decisions', input.decisions.join(','));
  if (input.dataStatuses?.length) query.set('dataStatuses', input.dataStatuses.join(','));
  if (input.riskLevels?.length) query.set('riskLevels', input.riskLevels.join(','));
  if (input.preliminaryDecisions?.length)
    query.set('preliminaryDecisions', input.preliminaryDecisions.join(','));
  if (input.minSearchVolume != null) query.set('minSearchVolume', String(input.minSearchVolume));
  if (input.minOrderedItems != null) query.set('minOrderedItems', String(input.minOrderedItems));
  if (input.minOrderConversionRate != null)
    query.set('minOrderConversionRate', String(input.minOrderConversionRate));
  if (input.maxProductsCount != null) query.set('maxProductsCount', String(input.maxProductsCount));
  if (input.minDemandSupplyRatio != null)
    query.set('minDemandSupplyRatio', String(input.minDemandSupplyRatio));
  if (input.view) query.set('view', input.view);
  if (input.limit) query.set('limit', String(input.limit));
  if (input.offset) query.set('offset', String(input.offset));
  if (input.sortField) query.set('sortField', input.sortField);
  if (input.sortDirection) query.set('sortDirection', input.sortDirection);
  return request<{ batchId: string | null; items: WbSelectionRow[]; total: number }>(
    `/wb/selection?${query.toString()}`,
  );
}

export async function getWbSelectionSummary(batchId?: string | null) {
  return request<WbSelectionSummary>(
    `/wb/selection/summary${batchId ? `?batchId=${encodeURIComponent(batchId)}` : ''}`,
  );
}

export async function getWbSelectionDetail(id: string) {
  return request<{
    candidate: WbSelectionRow;
    cluster: WbSelectionEntityCluster | null;
    events: Array<Record<string, unknown>>;
  }>(`/wb/selection/${id}`);
}

export async function estimateWbSelectionDepth(batchId: string, selection: WbDepthSelection) {
  return request<WbDepthEstimate>(`/wb/selection/batches/${batchId}/depth/estimate`, {
    method: 'POST',
    body: JSON.stringify(selection),
  });
}

export async function startWbSelectionDepth(batchId: string, selection: WbDepthSelection) {
  return request<{ job: JobSummary; estimate: WbDepthEstimate }>(
    `/wb/selection/batches/${batchId}/depth/start`,
    {
      method: 'POST',
      body: JSON.stringify({ confirmed: true, selection }),
    },
  );
}

export async function updateWbSelectionDecisions(rows: WbSelectionRow[], decision: UserDecision) {
  return request<{ updated: WbSelectionRow[]; conflicts: string[] }>('/wb/selection/decisions', {
    method: 'PATCH',
    body: JSON.stringify({
      decision,
      records: rows.map((row) => ({ id: row.id, expectedVersion: row.version })),
    }),
  });
}

export async function retryWbSelectionStage(
  batchId: string,
  stage: 'all' | 'translation' | 'topvisor' | 'wb_match',
) {
  return request<{ job: JobSummary | null; targetCount: number }>(
    `/wb/selection/batches/${batchId}/retry/${stage}`,
    { method: 'POST' },
  );
}

export async function exportWbSelectionFiltered(input: {
  batchId?: string | null;
  text?: string;
  decisions?: string[];
  dataStatuses?: string[];
  riskLevels?: string[];
  preliminaryDecisions?: string[];
  minSearchVolume?: number | null;
  minOrderedItems?: number | null;
  minOrderConversionRate?: number | null;
  maxProductsCount?: number | null;
  minDemandSupplyRatio?: number | null;
  view?: WbSelectionView;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
}) {
  const rows: WbSelectionRow[] = [];
  let offset = 0;
  while (true) {
    const page = await searchWbSelection({ ...input, limit: 200, offset });
    rows.push(...page.items);
    offset += page.items.length;
    if (!page.items.length || offset >= page.total) break;
  }
  const header = [
    '俄语关键词',
    '中文商品',
    '标准商品实体',
    'WB类目',
    '当前需求',
    '需求趋势',
    '订单转化率',
    '需求供给比',
    '季节性',
    '风险',
    'WB Jam初筛分',
    '初筛结论',
    '最终机会分',
    '最终结论',
    '补全阶段',
    '补全完整度',
    '数据状态',
    '失败代码',
    '失败摘要',
    '更新时间',
  ];
  const quote = (value: unknown) => {
    const text =
      value == null
        ? ''
        : typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
          ? String(value)
          : (JSON.stringify(value) ?? '');
    return `"${text.replaceAll('"', '""')}"`;
  };
  const lines = rows.map((row) =>
    [
      row.originalKeywordRu,
      row.chineseTranslationZh,
      row.standardProductNameZh,
      row.categoryNameZh ?? row.categoryNameRu,
      row.frequencyPhrase ?? row.searchVolumeCurrent,
      row.demandTrendRate,
      row.orderConversionRate,
      row.demandSupplyRatio,
      row.seasonalityType,
      row.riskLevel,
      row.preliminaryScore,
      row.preliminaryDecision,
      row.finalScore,
      row.finalDecision,
      row.enrichmentStage,
      row.enrichmentCompleteness,
      row.dataStatus,
      row.failureCode,
      row.errorSummary,
      row.updatedAt,
    ]
      .map(quote)
      .join(','),
  );
  const blob = new Blob([`\uFEFF${header.map(quote).join(',')}\n${lines.join('\n')}`], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `wb-selection-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
  return rows.length;
}

export async function listJobs() {
  return request<{ items: JobSummary[] }>('/jobs?status=active,recent');
}

export async function controlJob(
  jobId: string,
  action: 'pause' | 'resume' | 'cancel' | 'retry' | 'dismiss',
) {
  return request<JobSummary>(`/jobs/${jobId}/${action}`, { method: 'POST' });
}

export async function listUsers() {
  return request<{ items: CurrentUser[] }>('/admin/users');
}

export async function createUser(input: {
  username: string;
  password: string;
  role: 'admin' | 'user';
}) {
  return request<CurrentUser>('/admin/users', { method: 'POST', body: JSON.stringify(input) });
}

export async function updateUser(
  user: CurrentUser,
  patch: { role?: 'admin' | 'user'; isActive?: boolean },
) {
  return request<CurrentUser>(`/admin/users/${user.id}`, {
    method: 'PATCH',
    body: JSON.stringify({ ...patch, expectedVersion: user.version }),
  });
}

export async function resetUserPassword(user: CurrentUser, password: string) {
  return request<CurrentUser>(`/admin/users/${user.id}/reset-password`, {
    method: 'POST',
    body: JSON.stringify({ password, expectedVersion: user.version }),
  });
}

export async function listImportBatches() {
  return request<{ items: ImportBatchSummary[] }>('/imports/batches');
}

export async function undoImportBatch(id: string) {
  return request<ImportBatchSummary>(`/admin/imports/batches/${id}/undo`, { method: 'POST' });
}

export async function listBlacklist() {
  return request<{ items: BlacklistSummary[] }>('/admin/blacklist');
}

export async function releaseBlacklist(record: BlacklistSummary) {
  return request<BlacklistSummary>(
    `/admin/blacklist/${record.id}?expectedVersion=${record.version}`,
    { method: 'DELETE' },
  );
}

export async function runKeywordAction(
  platform: Platform,
  records: string[],
  action: 'ai_review' | 'recalculate_score' | 'rerun_risk' | 'refresh_dictionary',
) {
  return request<JobSummary>(`/platforms/${platform}/keywords/actions`, {
    method: 'POST',
    body: JSON.stringify({ action, records }),
  });
}

export async function getKeywordDetail(platform: Platform, keywordId: string) {
  return request<KeywordDetail>(`/platforms/${platform}/keywords/${keywordId}/detail`);
}

export async function refreshCompetitors(
  platform: Platform,
  keywordId: string,
  links: string[] = [],
) {
  return request<{ job: JobSummary | null; fetchId: string | null; cacheHit: boolean }>(
    `/platforms/${platform}/keywords/${keywordId}/competitors/refresh`,
    { method: 'POST', body: JSON.stringify({ links, force: true }) },
  );
}

export async function updateCompetitorSample(
  platform: Platform,
  keywordId: string,
  item: { id: string; selected: boolean; locked: boolean; version: number },
  change: { selected?: boolean; locked?: boolean },
) {
  return request<KeywordDetail>(
    `/platforms/${platform}/keywords/${keywordId}/competitors/sample/${item.id}`,
    { method: 'PATCH', body: JSON.stringify({ ...change, expectedVersion: item.version }) },
  );
}

export async function extractProductMaterial(
  platform: Platform,
  keywordId: string,
  rawText: string,
) {
  return request<{
    material: { id: string; rawText: string };
    attributes: Array<{
      id: string;
      name: string;
      value: string;
      sourceExcerpt: string;
      version: number;
    }>;
  }>(`/platforms/${platform}/keywords/${keywordId}/materials/extract`, {
    method: 'POST',
    body: JSON.stringify({ rawText, sourceLink: null }),
  });
}

export async function updateExtractedAttribute(
  platform: Platform,
  keywordId: string,
  attribute: { id: string; value: string; version: number },
  value: string,
) {
  return request(
    `/platforms/${platform}/keywords/${keywordId}/materials/attributes/${attribute.id}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ value, expectedVersion: attribute.version }),
    },
  );
}

export async function saveKeywordRelations(
  platform: Platform,
  keywordId: string,
  relations: KeywordDetail['relatedKeywords'],
) {
  return request<{ items: KeywordDetail['relatedKeywords'] }>(
    `/platforms/${platform}/keywords/${keywordId}/keyword-relations`,
    {
      method: 'PUT',
      body: JSON.stringify({
        relations: relations.map(
          ({ keywordText, chineseTranslation, usageKind, selected, relevance }) => ({
            keywordText,
            chineseTranslation,
            usageKind,
            selected,
            relevance,
          }),
        ),
      }),
    },
  );
}

export async function createProductCardDrafts(
  platform: Platform,
  keywordId: string,
  input: {
    productName: string;
    materialId: string | null;
    titleKeywords: string[];
    descriptionKeywords: string[];
    category: string | null;
    candidates: number;
  },
) {
  return request<{ drafts: ProductCardDraft[] }>(
    `/platforms/${platform}/keywords/${keywordId}/product-cards/draft`,
    { method: 'POST', body: JSON.stringify(input) },
  );
}

export async function saveProductCard(
  platform: Platform,
  keywordId: string,
  draft: ProductCardDraft,
) {
  return request(`/platforms/${platform}/keywords/${keywordId}/product-cards`, {
    method: 'POST',
    body: JSON.stringify(draft),
  });
}

export async function listProductAnalyses() {
  return request<{ items: ProductAnalysisDetail[] }>('/product-analyses');
}

export async function interpretProductIdea(input: { idea: string; analysisMode: AnalysisMode }) {
  return request<ProductIdeaInterpretation>('/product-analyses/interpret', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function createResearchRun(input: {
  idea: string;
  entityZh: string;
  productTypeZh: string | null;
  useCases: string[];
  coreQueries: Array<{
    keywordRu: string;
    translationZh: string | null;
    confidence: number;
  }>;
  analysisMode: AnalysisMode;
}) {
  return request<ResearchRun>('/api/research/runs', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function generateSeoPrompt(input: GenerateSeoPromptRequest) {
  return request<SeoPromptGenerationResult>('/api/selection-analysis/generate-seo-prompt', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function extractSeoProduct(input: ExtractSeoProductRequest) {
  return request<SeoProductExtractionResult>('/api/selection-analysis/extract-seo-product', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function getResearchRun(runId: string) {
  return request<ResearchRun>(`/api/research/runs/${runId}/suggestions`);
}

export async function refreshResearchSuggestions(runId: string, force = false) {
  return request<ResearchRun>(`/api/research/runs/${runId}/suggestions/refresh`, {
    method: 'POST',
    body: JSON.stringify({ force }),
  });
}

export async function getWordstatVolumes(runId: string) {
  return request<WordstatVolumeSnapshot>(`/api/research/runs/${runId}/wordstat/volumes`);
}

export async function getSeasonalityEstimate(runId: string, keyword: string, force = false) {
  const search = new URLSearchParams({ keyword });
  if (force) search.set('force', 'true');
  return request<SeasonalityEstimate>(`/api/research/runs/${runId}/seasonality?${search}`);
}

export async function refreshWordstatVolumes(runId: string, force = true) {
  return request<{ job: JobSummary | null }>(
    `/api/research/runs/${runId}/wordstat/volumes/refresh`,
    { method: 'POST', body: JSON.stringify({ force }) },
  );
}

export async function selectResearchKeywords(runId: string, keywordIds: string[]) {
  return request<ResearchRun>(`/api/research/runs/${runId}/keywords/select`, {
    method: 'POST',
    body: JSON.stringify({ keywordIds }),
  });
}

export async function markWordstatOpened(runId: string, keywordId: string) {
  return request<ResearchSuggestion>(`/api/research/runs/${runId}/wordstat/opened`, {
    method: 'POST',
    body: JSON.stringify({ keywordId }),
  });
}

export async function saveManualWordstat(
  runId: string,
  input: {
    keywordId: string;
    frequency: number | null;
    period: string | null;
    trend: ResearchSuggestion['wordstatTrend'];
    notes: string | null;
  },
) {
  return request<ResearchSuggestion>(`/api/research/runs/${runId}/wordstat/manual`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function refreshResearchCompetitors(runId: string, platforms: Platform[]) {
  return request<{
    items: Array<{
      suggestionId: string;
      keywordRu: string;
      platform: Platform;
      keywordId: string;
      cacheHit: boolean;
      job: JobSummary | null;
    }>;
  }>(`/api/research/runs/${runId}/competitors/refresh`, {
    method: 'POST',
    body: JSON.stringify({ platforms }),
  });
}

export async function createResearchDecision(
  runId: string,
  competitorEvidence: Array<{
    platform: Platform;
    keywordId: string;
    itemCount: number;
    relevantCount: number;
    medianPrice: number | null;
    medianReviews: number | null;
    brandCount: number | null;
  }>,
) {
  return request<{
    decision: NonNullable<ResearchRun['state']['decision']>;
    reasons: Array<{ category: string; summary: string }>;
    run: ResearchRun;
  }>(`/api/research/runs/${runId}/decision`, {
    method: 'POST',
    body: JSON.stringify({ competitorEvidence }),
  });
}

export async function createProductAnalysis(input: CreateProductAnalysisInput) {
  return request<{
    requiresResolution: boolean;
    requestHash?: string;
    candidates?: Array<{
      product: ProductAnalysisDetail['product'];
      score: number;
      reasons: string[];
    }>;
    detail?: ProductAnalysisDetail;
  }>('/product-analyses', { method: 'POST', body: JSON.stringify(input) });
}

export async function restoreProductAnalysis(
  analysisId: string,
  versionId: string,
  expectedVersion: number,
) {
  return request<ProductAnalysisDetail>(
    `/product-analyses/${analysisId}/versions/${versionId}/restore`,
    { method: 'POST', body: JSON.stringify({ expectedVersion }) },
  );
}

export async function requestExternalAnalysisData(
  analysisId: string,
  sources: string[],
  platforms: Platform[],
) {
  return request<{ items: JobSummary[] }>(`/product-analyses/${analysisId}/external-data`, {
    method: 'POST',
    body: JSON.stringify({ sources, platforms }),
  });
}

export async function listCostProfiles(platform?: Platform) {
  return request<{ items: CostProfileSummary[] }>(
    `/cost-profiles${platform ? `?platform=${platform}` : ''}`,
  );
}

export async function createCostProfile(input: {
  platform: Platform;
  fulfillmentMethod: string;
  name: string;
  defaultCurrency: Currency;
  config: Record<string, unknown>;
}) {
  return request<CostProfileSummary>('/admin/cost-profiles', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function listExchangeRates() {
  return request<{ items: ExchangeRateSummary[] }>('/exchange-rates');
}

export async function createExchangeRate(input: {
  baseCurrency: Currency;
  quoteCurrency: Currency;
  rate: number;
  source: string;
  rateDate: string;
  status: 'official' | 'settlement' | 'manual' | 'stale_fallback';
}) {
  return request<ExchangeRateSummary>('/admin/exchange-rates', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function searchDictionary(q = '') {
  return request<{ items: DictionarySummary[] }>(`/dictionary?q=${encodeURIComponent(q)}`);
}

export async function createDictionaryEntry(input: {
  canonicalKeyword: string;
  chineseTranslation: string | null;
  standardProductName: string | null;
  productType: string | null;
  mainKeyword: string | null;
  aliases: Array<{ value: string; kind: 'synonym' | 'grammar_variant' | 'related' }>;
  locked: boolean;
}) {
  return request<DictionarySummary>('/admin/dictionary', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function listPrompts() {
  return request<{ items: PromptSummary[] }>('/admin/prompts');
}

export async function listAiRuns() {
  return request<{ items: Array<Record<string, unknown>> }>('/admin/ai-runs');
}

export async function listNotifications() {
  return request<{ items: AdminNotification[] }>('/admin/notifications');
}

export async function acknowledgeNotification(id: string) {
  return request<AdminNotification>(`/admin/notifications/${id}/acknowledge`, { method: 'POST' });
}

export interface BackupSummary {
  id: string;
  status: string;
  triggerKind: string;
  fileName: string | null;
  sha256: string | null;
  sizeBytes: number | null;
  protected: boolean;
  verifiedAt: string | null;
  completedAt: string | null;
  prunedAt: string | null;
  version: number;
  createdAt: string;
}

export interface RestoreSummary {
  id: string;
  backupId: string;
  status: string;
  reason: string;
  failureStage: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface TestSnapshotSummary {
  id: string;
  status: string;
  platforms: Platform[];
  requestedSampleSize: number;
  copiedRowCount: number;
  sourceEnvironmentId: string;
  targetEnvironmentId: string;
  excludedSensitiveKinds: string[];
  createdAt: string;
  completedAt: string | null;
}

export interface ReleaseSummary {
  id: string;
  sourceKind: 'github_release' | 'simulation';
  tag: string;
  releaseVersion: string;
  releaseNotes: string;
  migrationSummary: string;
  status: string;
  failureStage: string | null;
  events: Array<{ stage: string; status: string }>;
  checkedAt: string;
  completedAt: string | null;
}

export interface OperationsHealth {
  status: 'ok' | 'warning' | 'critical';
  checks: {
    database: string;
    disk: string;
    queue: string;
    backup: string;
  };
  diskUsedPercent: number | null;
  queuedJobs: number;
  latestBackupAt: string | null;
  maintenance: Record<string, unknown>;
  testSnapshotConfigured: boolean;
}

export async function listBackups() {
  return request<{ items: BackupSummary[] }>('/admin/backups');
}

export async function createBackup(protectedBackup = false) {
  return request<{ backup: BackupSummary; job: JobSummary }>('/admin/backups', {
    method: 'POST',
    body: JSON.stringify({ protected: protectedBackup }),
  });
}

export async function restoreBackup(input: {
  id: string;
  expectedVersion: number;
  confirmation: string;
  reason: string;
}) {
  return request<{ restore: RestoreSummary; job: JobSummary }>(
    `/admin/backups/${input.id}/restore`,
    {
      method: 'POST',
      body: JSON.stringify({
        expectedVersion: input.expectedVersion,
        confirmation: input.confirmation,
        reason: input.reason,
      }),
    },
  );
}

export async function listRestores() {
  return request<{ items: RestoreSummary[] }>('/admin/restores');
}

export async function listTestSnapshots() {
  return request<{
    configured: boolean;
    targetEnvironmentId: string | null;
    items: TestSnapshotSummary[];
  }>('/admin/test-snapshots');
}

export async function createTestSnapshot(input: { platforms: Platform[]; sampleSize: number }) {
  return request<{ snapshot: TestSnapshotSummary; job: JobSummary }>('/admin/test-snapshots', {
    method: 'POST',
    body: JSON.stringify({ ...input, confirmOverwrite: true }),
  });
}

export async function listReleases() {
  return request<{ items: ReleaseSummary[]; currentVersion: string }>('/admin/releases');
}

export async function checkReleases() {
  return request<ReleaseSummary>('/admin/releases/check', { method: 'POST' });
}

export async function registerReleaseSimulation(input: {
  tag: string;
  releaseNotes: string;
  failSafeDigest: string;
}) {
  return request<ReleaseSummary>('/admin/releases/simulations', {
    method: 'POST',
    body: JSON.stringify({
      source: 'simulation',
      tag: input.tag,
      version: input.tag,
      releaseNotes: input.releaseNotes,
      migrationSummary: '本地模拟 migration dry-run',
      assetName: `rfz-xp-${input.tag}.tar.gz`,
      assetSha256: input.failSafeDigest,
      assetUrl: `https://example.test/releases/${input.tag}/rfz-xp.tar.gz`,
    }),
  });
}

export async function simulateReleaseInstall(input: { releaseId: string; failAt?: string }) {
  return request<ReleaseSummary>('/admin/releases/simulate-install', {
    method: 'POST',
    body: JSON.stringify({
      releaseId: input.releaseId,
      confirmation: `INSTALL ${input.releaseId}`,
      ...(input.failAt ? { failAt: input.failAt } : {}),
    }),
  });
}

export async function getOperationsHealth() {
  return request<OperationsHealth>('/admin/operations/health');
}

export async function runOperationsCleanup() {
  return request<Record<string, unknown>>('/admin/operations/cleanup', {
    method: 'POST',
    body: JSON.stringify({ confirmation: 'RUN CLEANUP' }),
  });
}
